import React from 'react';
import PropTypes from 'prop-types';

import { reduxForm, Form, Field } from 'redux-form/immutable';
import { isEmail, isMobilePhone } from 'validator';
import makeEditableFieldUtils from '@eGroupTeam/hooks/immutable/makeEditableFieldUtils';

import FormFieldGroup from '@eGroupTeam/material/FormFieldGroup';
import EditableField from '@eGroupTeam/material/EditableField';
import EditableFieldShowing from '@eGroupTeam/material/EditableFieldShowing';
import TextLoadingField from '@eGroupTeam/material-form/immutable/TextLoadingField';
import Typography from '@eGroupTeam/material/Typography';
import apis from 'redux/apis';

export const FORM = 'userEditForm';

const useEditableFieldUtils = makeEditableFieldUtils(FORM);

const validate = (values) => {
  const errors = {};
  if (!values.get('userName')) {
    errors.userName = '「姓名」是必填欄位';
  }
  if (values.get('userEmail') && !isEmail(values.get('userEmail'))) {
    errors.userEmail = '「Email」格式錯誤';
  }
  return errors;
};

const asyncValidate = (values, dispatch, props) => {
  if (!isMobilePhone(values.get('userPhone'), 'zh-TW')) {
    return new Promise((resolve, reject) => {
      // eslint-disable-next-line prefer-promise-reject-errors
      reject('手機號碼格式錯誤');
    }).catch(() => {
      const error = {
        userPhone: '手機號碼格式錯誤',
      };
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw error;
    });
  }
  return apis
    .fetchPostCheckUserDuplicate({
      organizationId: props.organizationId,
      userId: values.get('userId'),
      userPhone: values.get('userPhone'),
    })
    .catch(() => {
      const error = {
        userPhone: '手機號碼已存在',
      };
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw error;
    });
};

const UserEditForm = ({ handleSubmit }) => {
  const { formValues, handleSave, handleClose } = useEditableFieldUtils();

  return (
    <Form onSubmit={handleSubmit}>
      <FormFieldGroup required label="姓名">
        <EditableField
          onSaveClick={handleSave}
          onCloseClick={handleClose}
          disableClickAwayCloseEditing
          style={{ marginLeft: -8 }}
        >
          <EditableFieldShowing>
            <Typography variant="body1">
              {formValues && formValues.get('userName')}
            </Typography>
          </EditableFieldShowing>
          <Field
            name="userName"
            margin="normal"
            component={TextLoadingField}
            fullWidth
          />
        </EditableField>
      </FormFieldGroup>
      <FormFieldGroup label="單位">
        <EditableField
          onSaveClick={handleSave}
          onCloseClick={handleClose}
          disableClickAwayCloseEditing
          style={{ marginLeft: -8 }}
        >
          <EditableFieldShowing>
            <Typography variant="body1">
              {formValues && formValues.get('userOrganizationName')}
            </Typography>
          </EditableFieldShowing>
          <Field
            name="userOrganizationName"
            margin="normal"
            component={TextLoadingField}
            fullWidth
          />
        </EditableField>
      </FormFieldGroup>
      <FormFieldGroup label="職稱">
        <EditableField
          onSaveClick={handleSave}
          onCloseClick={handleClose}
          disableClickAwayCloseEditing
          style={{ marginLeft: -8 }}
        >
          <EditableFieldShowing>
            <Typography variant="body1">
              {formValues && formValues.get('userJobTitle')}
            </Typography>
          </EditableFieldShowing>
          <Field
            name="userJobTitle"
            margin="normal"
            component={TextLoadingField}
            fullWidth
          />
        </EditableField>
      </FormFieldGroup>
      <FormFieldGroup label="Email">
        <EditableField
          onSaveClick={handleSave}
          onCloseClick={handleClose}
          disableClickAwayCloseEditing
          style={{ marginLeft: -8 }}
        >
          <EditableFieldShowing>
            <Typography variant="body1">
              {formValues && formValues.get('userEmail')}
            </Typography>
          </EditableFieldShowing>
          <Field
            name="userEmail"
            margin="normal"
            component={TextLoadingField}
            fullWidth
          />
        </EditableField>
      </FormFieldGroup>
      <FormFieldGroup label="電話">
        <EditableField
          onSaveClick={handleSave}
          onCloseClick={handleClose}
          disableClickAwayCloseEditing
          style={{ marginLeft: -8 }}
        >
          <EditableFieldShowing>
            <Typography variant="body1">
              {formValues && formValues.get('userPhone')}
            </Typography>
          </EditableFieldShowing>
          <Field
            name="userPhone"
            margin="normal"
            component={TextLoadingField}
            fullWidth
          />
        </EditableField>
      </FormFieldGroup>
    </Form>
  );
};

UserEditForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
};

export default reduxForm({
  form: FORM,
  validate,
  asyncValidate,
  asyncChangeFields: ['userPhone'],
  shouldAsyncValidate: ({
    syncValidationPasses,
    trigger,
    pristine,
    initialized,
    blurredField,
  }) => {
    if (!syncValidationPasses || blurredField !== 'userPhone') {
      return false;
    }
    switch (trigger) {
      // @ts-ignore
      case 'change':
      case 'blur':
        return true;
      case 'submit':
        return !pristine || !initialized;
      default:
        return false;
    }
  },
})(UserEditForm);
